// Deep link 캐치 + Supabase 세션 적용 (Step 8.3)
// - Capacitor App 플러그인이 OS deep link 이벤트(`appUrlOpen`)를 발행
// - URL 형식 2종:
//   1) Supabase Google OAuth → com.sotda.app://auth/callback#access_token=...&refresh_token=...
//   2) Naver 자체 라우트 → com.sotda.app://auth/callback?token_hash=...&type=email&next=...

import { App, type URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { supabase } from "./supabase";
import { isOnboarded } from "./onboarding";
import { PENDING_ID_LINK_KEY } from "./oauth";
import { flushPendingMbtiDiagnosis } from "./pendingMbtiDiagnosis";

const REDIRECT_PREFIX = "com.sotda.app://auth/callback";
const API_BASE = (import.meta.env.VITE_API_BASE as string) || "https://sotda.com";

// ⚠ 보안(2026-06-21 B10 P2 ⑤): deep link URL에는 access_token·refresh_token·token_hash·
//    link_ticket 등 세션 탈취 가능한 토큰이 그대로 들어있다. console.log(url) 하면 Capacitor
//    native 로그(Console.app·logcat)에 평문으로 남아 토큰 노출면이 된다. 디버그용 로그는
//    민감 파라미터 값을 가리고 "키 이름 + 도착 여부"만 남긴다.
const SENSITIVE_KEYS = new Set([
  "access_token", "refresh_token", "token_hash", "link_ticket", "code", "provider_token",
]);
function redactUrl(raw: string): string {
  try {
    const u = new URL(raw);
    const mask = (sp: URLSearchParams) => {
      for (const k of Array.from(sp.keys())) {
        if (SENSITIVE_KEYS.has(k)) sp.set(k, "<redacted>");
      }
    };
    mask(u.searchParams);
    // hash(fragment)에도 토큰이 옴(구글 implicit flow) — 키 이름만 남기고 값 제거
    const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
    mask(frag);
    const fragStr = frag.toString();
    return `${u.protocol}//${u.host}${u.pathname}${u.search}${fragStr ? "#" + fragStr : ""}`;
  } catch {
    return "<unparseable-url>";
  }
}

export type DeeplinkResult =
  | { ok: true; next: string }
  | { ok: false; error: string };

export async function handleAuthCallbackUrl(url: string): Promise<DeeplinkResult> {
  const parsed = new URL(url);
  const query = parsed.searchParams;
  // Capacitor App 플러그인은 url 그대로 전달 — hash는 string으로 직접 파싱
  const hashStr = parsed.hash?.replace(/^#/, "") ?? "";
  const fragment = new URLSearchParams(hashStr);

  const errorParam = fragment.get("error") || query.get("error");
  if (errorParam) {
    return { ok: false, error: errorParam };
  }

  // 흐름 1) Naver 자체 라우트 — token_hash 기반 verifyOtp
  const tokenHash = query.get("token_hash");
  if (tokenHash) {
    const type = (query.get("type") || "email") as "email" | "magiclink" | "recovery";
    const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
    if (error) return { ok: false, error: error.message };
    const next = query.get("next") || "/";
    return { ok: true, next };
  }

  // 흐름 2) Supabase Google OAuth — fragment 안 access_token + refresh_token
  const accessToken = fragment.get("access_token");
  const refreshToken = fragment.get("refresh_token");
  if (accessToken && refreshToken) {
    const { error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true, next: "/" };
  }

  // PKCE 흐름 — 웹 Google OAuth·이메일 확인 링크는 query의 code로 돌아올 수 있다.
  const code = query.get("code");
  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) return { ok: false, error: error.message };
    return { ok: true, next: query.get("next") || "/" };
  }

  return { ok: false, error: "missing_token" };
}

// 계정연동(link) 콜백 — link_ticket을 Bearer와 함께 confirm API에 전달
// 로그인 흐름(token_hash)과 분리: 세션을 새로 만들지 않고 현재 계정에 식별자만 추가한다.
// 결과는 'sotda:auth:link-result' CustomEvent로 AccountLinkScreen에 전달.
async function handleLinkCallback(parsed: URL): Promise<Record<string, unknown>> {
  const ticket = parsed.searchParams.get("link_ticket") ?? "";
  const provider = parsed.searchParams.get("provider") ?? "";

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    return { provider, ok: false, error: "not_logged_in" };
  }
  try {
    const res = await fetch(`${API_BASE}/api/auth/link/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ ticket }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { provider, ok: false, error: data?.error || "link_failed" };
    }
    return { provider, ok: true, already: !!data?.already };
  } catch {
    return { provider, ok: false, error: "network" };
  }
}

// 구글 계정연동(linkIdentity) 콜백 — 로그인과 같은 URL로 돌아오지만 세션을 새로 만들지 않고
// 현재 계정에 구글 identity가 붙었는지 확인만 한다 (연동은 OAuth 교환 시 Supabase가 서버측에서 처리).
// 결과는 'sotda:auth:link-result' CustomEvent로 AccountLinkScreen에 전달.
async function handleIdentityLinkCallback(parsed: URL, provider: string): Promise<Record<string, unknown>> {
  const hashStr = parsed.hash?.replace(/^#/, "") ?? "";
  const fragment = new URLSearchParams(hashStr);

  // 사용자 취소·Supabase 거부 (예: 이미 다른 계정에 연동된 구글)
  const errorParam = parsed.searchParams.get("error") || fragment.get("error");
  if (errorParam) {
    return { provider, ok: false, error: errorParam };
  }

  // implicit flow: fragment의 갱신 토큰으로 세션을 최신화(새 identity 반영)
  const accessToken = fragment.get("access_token");
  const refreshToken = fragment.get("refresh_token");
  if (accessToken && refreshToken) {
    const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
    if (error) return { provider, ok: false, error: error.message };
  } else {
    // PKCE flow 대비: ?code= 가 있으면 교환
    const code = parsed.searchParams.get("code");
    if (code) {
      const { error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) return { provider, ok: false, error: error.message };
    }
  }

  // 서버 기준으로 구글 identity가 실제로 붙었는지 확인
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user) return { provider, ok: false, error: "not_logged_in" };
  const linked = (data.user.identities || []).some((i) => i.provider === provider);
  return linked
    ? { provider, ok: true }
    : { provider, ok: false, error: "link_failed" };
}

export type OAuthCallbackResult =
  | { kind: "login"; result: DeeplinkResult }
  | { kind: "link"; detail: Record<string, unknown> };

/** 네이티브 deep link와 웹 /auth/callback이 공유하는 콜백 분기. */
export async function processOAuthCallbackUrl(url: string): Promise<OAuthCallbackResult> {
  const parsed = new URL(url);
  if (parsed.searchParams.get("link_ticket")) {
    return { kind: "link", detail: await handleLinkCallback(parsed) };
  }

  const pending = await Preferences.get({ key: PENDING_ID_LINK_KEY });
  if (pending.value) {
    await Preferences.remove({ key: PENDING_ID_LINK_KEY });
    let info: { provider?: string; ts?: number } = {};
    try { info = JSON.parse(pending.value); } catch { /* 형식오류 무시 */ }
    if (info.provider && info.ts && Date.now() - info.ts < 5 * 60 * 1000) {
      return { kind: "link", detail: await handleIdentityLinkCallback(parsed, info.provider) };
    }
  }

  return { kind: "login", result: await handleAuthCallbackUrl(url) };
}

let attached = false;

export function attachDeepLinkListener(): void {
  if (!Capacitor.isNativePlatform()) return;
  if (attached) return;
  attached = true;

  App.addListener("appUrlOpen", async (event: URLOpenListenerEvent) => {
    const url = event.url;
    // 디버그 — deep link 발사 자체가 도착했는지 (시스템 브라우저→앱 깨우기 단계 검증)
    // ⚠ 토큰 마스킹(B10 P2 ⑤): url 원문엔 access_token 등이 있으므로 redactUrl로 가린다
    console.log("[deeplink] appUrlOpen fired, url =", redactUrl(url));
    if (!url?.startsWith(REDIRECT_PREFIX)) return;

    // 콜백 URL이 도착했다는 신호 — 세션 교환(네트워크) 전에 먼저 쏜다.
    //   LoginPage는 이 신호 없이 시스템 브라우저가 닫히면 "사용자가 취소"로 판정한다(8/22 메모 9).
    window.dispatchEvent(new CustomEvent("sotda:auth:callback-received"));

    const callback = await processOAuthCallbackUrl(url);

    // 시스템 브라우저 닫기 (성공/실패 모두)
    try {
      await Browser.close();
    } catch {
      // iOS는 close 미지원 — 무시
    }

    if (callback.kind === "link") {
      window.dispatchEvent(new CustomEvent("sotda:auth:link-result", { detail: callback.detail }));
      return;
    }

    const result = callback.result;
    console.log("[deeplink] handleAuthCallbackUrl result =", result);
    if (result.ok) {
      await flushPendingMbtiDiagnosis().catch((error) => {
        console.warn("[mbti/pending] 로그인 후 저장 실패", error);
      });
      // ④ 첫 로그인(미온보딩)이면 온보딩으로, 아니면 원래 next로 (HashRouter hash 이동)
      // 온보딩 플래그는 user.id 단위 (terra 버그10 — 같은 기기 신규 사용자 스킵 방지)
      const { data: { session } } = await supabase.auth.getSession();
      const target =
        result.next === "/" && !(await isOnboarded(session?.user?.id)) ? "/onboarding" : result.next;
      // replace — hash 할당(push)이면 로그인 화면이 방문 기록에 남아 안드로이드 뒤로·iOS 스와이프 뒤로가
      //   로그인 화면으로 되돌아간다(2026-09-11 에뮬레이터 A-3). 같은 문서의 해시만 바뀌어 리로드 없이 hashchange.
      window.location.replace(`${window.location.pathname}${window.location.search}#${target}`);
      // 글로벌 이벤트 — LoginPage가 받아서 UI 갱신 가능
      window.dispatchEvent(new CustomEvent("sotda:auth:login-success"));
    } else {
      window.dispatchEvent(
        new CustomEvent("sotda:auth:login-error", { detail: result.error })
      );
    }
  });
}
